import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, IconButton, Skeleton, Typography } from '@mui/material';
import {
  AddComment as AddCommentIcon,
  Close as CloseIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';
import { send_request } from '../../utils/Request';
import AgentSessionsPanel from './AgentSessionsPanel';
import SessionRow from './SessionRow';
import useAgentStore from './useAgentStore';

export const embeddedAgentPanelSx = {
  width: 420,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRight: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  position: 'relative',
  zIndex: 5,
  flexShrink: 0,
};

const DEFAULT_CHAT_UI_POLICY = {
  showSystemPromptInChat: false,
  showToolCallsAndOutputsInChat: true,
  showInterAgentMessagesInChat: false,
};

function isRequestError(response) {
  return Boolean(response?.response || response?.isAxiosError || response instanceof Error);
}

function extractRequestErrorMessage(response, fallback) {
  const status = response?.response?.status;
  const upstreamMessage =
    response?.response?.data?.error ||
    response?.response?.data?.message ||
    response?.message;
  if (status && upstreamMessage) {
    return `${fallback} (${status}): ${upstreamMessage}`;
  }
  return upstreamMessage || fallback;
}

export default function ScopedAgentChatPanel({
  panelTitle,
  agentName,
  agentDescription,
  provisionUrl,
  provisionAgent,
  provisionErrorMessage = 'Failed to provision agent',
  sessionFilter,
  emptySessionsMessage = 'No sessions yet.',
  onClose,
  chatContext,
  chatUiPolicy = DEFAULT_CHAT_UI_POLICY,
  composerDisabled = false,
  welcomePanelContent = null,
  inlineTaskQueueToggle = false,
  showWorkflowProvenanceInChat = false,
  panelSx = {},
}) {
  const [agentId, setAgentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newChatNonce, setNewChatNonce] = useState(0);
  const [showSessionsOverlay, setShowSessionsOverlay] = useState(false);
  const [selectSessionRequest, setSelectSessionRequest] = useState(null);

  const allSessions = useAgentStore(state => state.sessions);
  const sessionsLoading = useAgentStore(state => state.sessionsLoading);
  const sessions = useMemo(
    () => allSessions.filter(session => (sessionFilter ? sessionFilter(session) : true)),
    [allSessions, sessionFilter]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const provisionResult = provisionAgent
          ? await provisionAgent()
          : await send_request(provisionUrl, {}, {}, 'POST');
        if (cancelled) return;
        if (isRequestError(provisionResult)) {
          setError(extractRequestErrorMessage(provisionResult, provisionErrorMessage));
          return;
        }
        const nextAgentId =
          typeof provisionResult === 'string'
            ? provisionResult
            : provisionResult?.data?.agentId;
        if (nextAgentId) {
          setAgentId(nextAgentId);
        } else {
          setError(provisionErrorMessage);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || provisionErrorMessage);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [provisionAgent, provisionErrorMessage, provisionUrl]);

  const handleOpenSessions = useCallback(() => {
    if (agentId) {
      useAgentStore.getState().fetchSessions(agentId);
    }
    setShowSessionsOverlay(true);
  }, [agentId]);

  const handleSelectSession = useCallback(chatSessionId => {
    setShowSessionsOverlay(false);
    setSelectSessionRequest({ sessionId: chatSessionId, nonce: Date.now() });
  }, []);

  const panelStyle = { ...embeddedAgentPanelSx, ...panelSx };

  if (loading) {
    return (
      <Box sx={{ ...panelStyle, alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...panelStyle, alignItems: 'center', justifyContent: 'center', gap: 1, p: 2 }}>
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={panelStyle}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          height: '57px',
          minHeight: '57px',
          maxHeight: '57px',
          boxSizing: 'border-box',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {panelTitle || agentName}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={() => setNewChatNonce(n => n + 1)} title="New chat">
            <AddCommentIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleOpenSessions} title="Sessions">
            <ForumIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
        <AgentSessionsPanel
          agentId={agentId}
          agentName={agentName}
          agentDescription={agentDescription}
          mode="private"
          showSessionSidebar={false}
          newChatRequestNonce={newChatNonce}
          selectSessionRequest={selectSessionRequest}
          inlineTaskQueueToggle={inlineTaskQueueToggle}
          composerDisabled={composerDisabled}
          welcomePanelContent={welcomePanelContent}
          chatContext={chatContext}
          chatUiPolicy={chatUiPolicy}
        />

        {showSessionsOverlay && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'background.paper',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                Sessions
              </Typography>
              <IconButton size="small" onClick={() => setShowSessionsOverlay(false)}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {sessionsLoading ? (
                <Box sx={{ p: 2 }}>
                  {[1, 2, 3, 4, 5].map(i => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Skeleton variant="text" width="80%" height={18} />
                      <Skeleton variant="text" width="50%" height={14} />
                    </Box>
                  ))}
                </Box>
              ) : sessions.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.6 }}>
                    {emptySessionsMessage}
                  </Typography>
                </Box>
              ) : (
                sessions.map(session => (
                  <SessionRow
                    key={session.chatSessionId}
                    session={session}
                    selected={false}
                    onClick={() => handleSelectSession(session.chatSessionId)}
                    showWorkflowProvenanceInChat={showWorkflowProvenanceInChat}
                  />
                ))
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
